import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { AlertTriangle, CheckCircle2, Clock, XCircle } from "lucide-react";
import { api } from "@/lib/api";

const LOOK = {
  finish: { Icon: Clock, cls: "text-amber-600", title: "Pembayaran sedang diverifikasi",
    body: "Terima kasih. Kami sedang memastikan statusnya langsung ke Midtrans — status di bawah adalah status resmi, bukan tebakan dari halaman ini." },
  unfinish: { Icon: Clock, cls: "text-amber-600", title: "Pembayaran belum selesai",
    body: "Transaksi Anda belum selesai. Anda bisa mencoba lagi atau menyelesaikan pembayaran sebelum batas waktu." },
  error: { Icon: XCircle, cls: "text-lm-red", title: "Pembayaran gagal",
    body: "Terjadi kendala saat memproses pembayaran. Silakan coba lagi." },
};
const PAID = { Icon: CheckCircle2, cls: "text-emerald-600", title: "Pembayaran berhasil",
  body: "Pembayaran Anda sudah terkonfirmasi resmi oleh Midtrans. Booking Anda aman." };

export default function PaymentResult({ kind }) {
  const [params] = useSearchParams();
  const orderId = params.get("order_id") || "";
  const isSandbox = orderId.toUpperCase().startsWith("SANDBOX-LM-");
  const [status, setStatus] = useState(null);
  const [checking, setChecking] = useState(false);
  // "Berhasil" hanya ditampilkan kalau Midtrans memang mengonfirmasi lunas.
  const { Icon, cls, title, body } = status?.is_paid ? PAID : (LOOK[kind] || LOOK.error);

  const reconcile = React.useCallback(() => {
    if (!orderId) return;
    setChecking(true);
    // Status selalu diambil ulang dari Midtrans di server — parameter di URL tidak dipercaya.
    api.post(`/public/payment-status/${encodeURIComponent(orderId)}`)
      .then((r) => setStatus(r.data))
      .catch(() => setStatus(null))
      .finally(() => setChecking(false));
  }, [orderId]);

  useEffect(() => { reconcile(); }, [reconcile]);

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-5 py-16" data-testid={`payment-${kind}`}>
      <div className="w-full max-w-md bg-white border border-black/10 rounded-xl p-7 text-center">
        <Icon size={38} className={`mx-auto ${cls}`} />
        <h1 className="mt-4 font-display text-2xl">{title}</h1>
        <p className="mt-2 text-sm text-ink-soft">{body}</p>
        {isSandbox && (
          <div className="mt-5 rounded-lg border-2 border-lm-red bg-lm-red/10 p-3 flex items-start gap-2 text-left"
            data-testid="payment-sandbox-banner">
            <AlertTriangle size={16} className="text-lm-red shrink-0 mt-0.5" />
            <span className="text-xs font-semibold text-lm-red">
              MIDTRANS SANDBOX — TEST ONLY — JANGAN BAYAR DENGAN UANG ASLI
            </span>
          </div>
        )}
        {orderId && (
          <div className="mt-5 rounded-lg border border-black/10 bg-black/[0.02] p-3 text-left"
            data-testid="payment-status-box">
            <div className="text-[10px] uppercase tracking-widest text-ink-soft">Status terverifikasi</div>
            <div className="mt-1 text-sm" data-testid="payment-status-value">
              {checking ? "Memeriksa ke Midtrans…"
                : status ? `${status.status || "belum ada di Midtrans"}${status.is_paid ? " · LUNAS" : ""}`
                  : "Belum bisa diperiksa"}
            </div>
            {status?.message && <p className="mt-1 text-xs text-ink-soft">{status.message}</p>}
            <button data-testid="payment-recheck" disabled={checking} onClick={reconcile}
              className="mt-3 h-9 px-3.5 rounded-md border border-black/15 text-xs disabled:opacity-60">
              Periksa lagi
            </button>
          </div>
        )}
        <div className="mt-6 flex gap-2 justify-center">
          <Link to="/" data-testid="payment-home"
            className="h-11 px-5 rounded-full border border-black/15 text-sm inline-flex items-center">Kembali ke beranda</Link>
          {kind !== "finish" && (
            <Link to="/cek-booking" data-testid="payment-retry"
              className="h-11 px-5 rounded-full lm-btn-primary text-sm font-semibold inline-flex items-center">Coba lagi</Link>
          )}
        </div>
      </div>
    </div>
  );
}
